import { ComponentProps, ReactNode } from "react"
import { interpolate, spring, useCurrentFrame, useVideoConfig } from "remotion"
import { z } from "zod"

import { useCurrentScene } from "@postsunday/remotion-shared/hooks/useCurrentScene"
import { subtitleElementSchema } from "@postsunday/remotion-shared/lib/validations/element"
import { savedInputPropsSchema } from "@postsunday/remotion-shared/lib/validations/input-props"
import { DEFAULT_FPS } from "@postsunday/shared/lib/constants/server"
import { cn } from "@postsunday/shared/lib/utils"

export const SubtitlesAtChunk = ({
  clippingBasedProperties: { chunks: scenes },
  startFromFrame,
  endFrame,
  wordsAtATime,
  children,
  highlightBackgroundColor,
  highlightTextColor,
  highlightFontStrokeWidth,
  color,
  animationStyle,
  inactiveColor,
  ...props
}: {
  startFromFrame: number
  endFrame: number
  highlightBackgroundColor: string
  highlightTextColor: string
  highlightFontStrokeWidth?: string
  children?: (text: string, fullText: string) => ReactNode
  wordsAtATime: number
  animationStyle: z.infer<typeof subtitleElementSchema>["animationStyle"]
  inactiveColor: string | null | undefined
  borderRadius?: string | number
} & Omit<ComponentProps<"p">, "children"> &
  Pick<z.infer<typeof savedInputPropsSchema>, "clippingBasedProperties">) => {
  const { fps } = useVideoConfig()
  if (fps !== DEFAULT_FPS) {
    console.error(
      "SubtitlesAtChunk component is designed to work with 30fps videos. Please change the video settings to 30fps. This clip fps:",
      fps
    )
  }
  const {
    currentScene,
    currentWordIndexInCurrentScene,
    lastWordIndexInCurrentScene,
  } = useCurrentScene({
    chunks: scenes,
    startFromFrame,
    endFrame,
    wordsAtATime,
  })
  const { borderRadius, ...spanProps } = props
  return currentScene?.map(({ word, id, start }, i, subtitle) => (
    <span
      {...spanProps}
      key={id}
      className={cn(
        "relative inline-block whitespace-pre-wrap transition-all duration-100",
        props.className
      )}
    >
      {
        <span
          className={i === currentWordIndexInCurrentScene ? "block" : "hidden"}
          key={1}
        >
          <BackgroundHighlight
            highlightBackgroundColor={highlightBackgroundColor}
            animationStyle={animationStyle}
            borderRadius={borderRadius}
            fromFrame={Math.floor(start * DEFAULT_FPS) - startFromFrame}
          />
        </span>
      }
      <span
        className="relative z-10"
        key={2}
        style={{
          ...(i === currentWordIndexInCurrentScene
            ? { color: highlightTextColor }
            : i <= lastWordIndexInCurrentScene && inactiveColor
              ? { color: inactiveColor }
              : { color }),
          ...(i === currentWordIndexInCurrentScene &&
            !!highlightFontStrokeWidth && {
              // https://stackoverflow.com/questions/5687035/css-bolding-some-text-without-changing-its-containers-size
              // maintains container width!
              "-webkit-text-stroke-width": highlightFontStrokeWidth,
            }),
        }}
      >
        {children?.(word, subtitle.map((sub) => sub.word).join(""))}
      </span>
    </span>
  ))
}

function BackgroundHighlight({
  highlightBackgroundColor,
  animationStyle,
  borderRadius,
  fromFrame,
}: {
  highlightBackgroundColor: string
  animationStyle: z.infer<typeof subtitleElementSchema>["animationStyle"]
  borderRadius?: number | string
  fromFrame: number
}) {
  const fps = DEFAULT_FPS
  const frame = useCurrentFrame()
  const scaleSpring = spring({
    frame,
    fps,
    durationInFrames: 10,
    delay: fromFrame,
    // https://springs.remotion.dev/
    config: { damping: 9 },
  })

  const scale = interpolate(scaleSpring, [0, 1.25], [0.7, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  })

  return (
    <span
      className={cn(
        "absolute inset-x-[-5%] inset-y-0 z-0 overflow-hidden",
        typeof borderRadius === "string" && borderRadius !== "full"
          ? "mx-3"
          : typeof borderRadius === "number" && borderRadius < 15
            ? "mx-2"
            : typeof borderRadius === "number" &&
                borderRadius > 10 &&
                borderRadius < 15
              ? "mx-2"
              : animationStyle === "scale"
                ? "mx-1"
                : undefined
      )}
      style={{
        transform: animationStyle === "scale" ? `scaleX(${scale})` : undefined,
        backgroundColor: highlightBackgroundColor,
        borderRadius: borderRadius ? borderRadius : "8px",
      }}
    />
  )
}
